import React, { useState, useMemo } from 'react';
import {
    Search,
    Plus,
    Filter,
    ArrowUpDown,
    UploadCloud,
} from 'lucide-react';
import { MobileTabButton } from '../shared';
import { MobileLeadCard } from './MobileLeadCard';
import { MobileLeadDrawer } from '../drawers/MobileLeadDrawer';
import { MobileAddLeadModal } from '../modals/MobileAddLeadModal';
import { MobileImportModal } from '../modals/MobileImportModal';

/**
 * Mobile leads view with header, tabs, card list, drawers, and modals.
 */
export const MobileLeads = ({
    activeTab,
    setActiveTab,
    showAddModal,
    setShowAddModal,
    showImportModal,
    setShowImportModal,
    selectedLead,
    showDrawer,
    openLeadDrawer,
    closeDrawer,
    openMenu,
    toggleMenu,
    leads,
    executionMap = new Map(),
    lastContactMap = {},
    onAddLead,
    onUpdateStatus,
    onUpdateDetails,
    onArchiveLead,
    onUnarchiveLead,
    onDeleteLead,
    onUpdateTags,
    VALID_STATUSES,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name'); // 'name', 'lastContact', 'createdAt'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [automationFilter, setAutomationFilter] = useState('all');
    const [interestFilter, setInterestFilter] = useState('all');

    // Derive unique automation names and property addresses for filter dropdowns
    const automationNames = useMemo(() => {
        const names = new Set();
        executionMap.forEach((execs) => {
            execs.forEach(e => { if (e.automationName) names.add(e.automationName); });
        });
        return [...names].sort();
    }, [executionMap]);

    const propertyAddresses = useMemo(() => {
        const addresses = new Set();
        (leads || []).forEach(lead => {
            (lead.interestedProperties || []).forEach(prop => {
                if (prop.address) addresses.add(prop.address);
            });
        });
        return [...addresses].sort();
    }, [leads]);

    const activeFilterCount = (automationFilter !== 'all' ? 1 : 0) + (interestFilter !== 'all' ? 1 : 0);

    // Enrich leads with last contact data from conversations
    const enrichedLeads = (leads || []).map(lead => {
        const convo = lastContactMap[lead.id];
        return {
            ...lead,
            _lastContactAt: convo?.hasMessages ? convo.lastMessageAt : null,
        };
    });

    // Apply tab filtering
    const tabFilteredLeads = enrichedLeads.filter((lead) => {
        if (activeTab === 'all') return true;
        if (activeTab === 'active') return lead.status === 'Active' && !lead.archived;
        if (activeTab === 'closed') return lead.status === 'Closed' && !lead.archived;
        if (activeTab === 'archived') return lead.archived || lead.status === 'Archived';
        if (activeTab === 'cold') return lead.status === 'Cold' && !lead.archived;
        return true;
    });

    // Apply search filtering
    const searchFilteredLeads = searchQuery.trim() === ''
        ? tabFilteredLeads
        : tabFilteredLeads.filter((lead) => {
            const query = searchQuery.toLowerCase();
            const displayName = (lead.fullName || lead.name || '').toLowerCase();
            return (
                displayName.includes(query) ||
                (lead.email && lead.email.toLowerCase().includes(query)) ||
                (lead.phone && lead.phone.toLowerCase().includes(query)) ||
                (lead.address && lead.address.toLowerCase().includes(query))
            );
        });

    // Apply advanced filters (automation + interest)
    const advancedFilteredLeads = searchFilteredLeads.filter(lead => {
        if (automationFilter !== 'all') {
            const leadExecs = executionMap.get(lead.id) || [];
            if (automationFilter === 'none') {
                if (leadExecs.length > 0) return false;
            } else {
                if (!leadExecs.some(e => e.automationName === automationFilter)) return false;
            }
        }
        if (interestFilter !== 'all') {
            if (interestFilter === 'none') {
                if ((lead.interestedProperties || []).length > 0) return false;
            } else {
                const props = lead.interestedProperties || [];
                if (!props.some(p => p.address === interestFilter)) return false;
            }
        }
        return true;
    });

    // Helper to extract timestamp millis from Firestore Timestamp, Date, string, or number
    const toTimestamp = (val) => {
        if (!val) return 0;
        if (typeof val === 'object' && 'seconds' in val) return val.seconds * 1000;
        if (val instanceof Date) return val.getTime();
        if (typeof val === 'string') { const d = new Date(val); return isNaN(d.getTime()) ? 0 : d.getTime(); }
        if (typeof val === 'number') return val;
        return 0;
    };

    // Apply sorting
    const filteredLeads = [...advancedFilteredLeads].sort((a, b) => {
        let aVal, bVal;

        if (sortBy === 'name') {
            aVal = (a.fullName || a.name || '').toLowerCase();
            bVal = (b.fullName || b.name || '').toLowerCase();
        } else if (sortBy === 'lastContact') {
            aVal = toTimestamp(a._lastContactAt);
            bVal = toTimestamp(b._lastContactAt);
        } else if (sortBy === 'createdAt') {
            aVal = toTimestamp(a.createdAt);
            bVal = toTimestamp(b.createdAt);
        }

        if (typeof aVal === 'string') {
            return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        } else {
            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        }
    });

    const handleSortChange = (newSortBy) => {
        if (sortBy === newSortBy) {
            // Toggle sort order if clicking same sort option
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // New sort field, default to ascending
            setSortBy(newSortBy);
            setSortOrder('asc');
        }
        setShowSortMenu(false);
    };

    return (
        <>
            <header className="flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-notion-border z-20 sticky top-0">
                <div className="px-4 py-3 flex items-center justify-between">
                    <h1 className="text-lg font-semibold text-notion-text tracking-tight">Leads Database</h1>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="p-2 text-stone-500 hover:bg-notion-hover rounded-full transition-colors"
                        >
                            <UploadCloud className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="p-2 bg-stone-800 text-white rounded-lg shadow-sm active:scale-95 transition-transform flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            <span className="text-xs font-medium">Add</span>
                        </button>
                    </div>
                </div>

                <div className="px-4 pb-3 space-y-3 bg-white">
                    <div className="relative group w-full">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-notion-text-light" />
                        <input
                            type="text"
                            placeholder="Search leads..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-lg text-sm focus:outline-none transition-all placeholder-notion-text-light text-notion-text shadow-sm"
                        />
                    </div>

                    <div className="flex items-center gap-1 overflow-x-auto no-scrollbar pb-1">
                        <MobileTabButton active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
                            All Leads
                        </MobileTabButton>
                        <MobileTabButton active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
                            Active
                        </MobileTabButton>
                        <MobileTabButton active={activeTab === 'closed'} onClick={() => setActiveTab('closed')}>
                            Closed
                        </MobileTabButton>
                        <MobileTabButton active={activeTab === 'archived'} onClick={() => setActiveTab('archived')}>
                            Archived
                        </MobileTabButton>
                        <MobileTabButton active={activeTab === 'cold'} onClick={() => setActiveTab('cold')}>
                            Cold
                        </MobileTabButton>

                        <div className="w-px h-4 bg-stone-200 mx-1 flex-shrink-0"></div>

                        <div className="relative flex-shrink-0">
                            <button
                                onClick={() => { setShowFilterMenu(!showFilterMenu); setShowSortMenu(false); }}
                                className={`p-1.5 rounded-full border bg-white ${activeFilterCount > 0 ? 'border-stone-400 text-stone-800' : 'border-stone-200 text-stone-500'}`}
                            >
                                <Filter className="w-3.5 h-3.5" />
                            </button>
                            {activeFilterCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-stone-800 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center">
                                    {activeFilterCount}
                                </span>
                            )}
                            {showFilterMenu && (
                                <div className="absolute right-0 mt-1 bg-white border border-notion-border rounded-lg shadow-lg z-50 w-56 p-3 space-y-3">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Automation</label>
                                        <select
                                            value={automationFilter}
                                            onChange={(e) => setAutomationFilter(e.target.value)}
                                            className="w-full text-sm border border-notion-border rounded px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:border-stone-400"
                                        >
                                            <option value="all">All automations</option>
                                            <option value="none">No automation</option>
                                            {automationNames.map(name => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Interest</label>
                                        <select
                                            value={interestFilter}
                                            onChange={(e) => setInterestFilter(e.target.value)}
                                            className="w-full text-sm border border-notion-border rounded px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:border-stone-400"
                                        >
                                            <option value="all">All interests</option>
                                            <option value="none">No interest</option>
                                            {propertyAddresses.map(addr => (
                                                <option key={addr} value={addr}>{addr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {activeFilterCount > 0 && (
                                        <button
                                            onClick={() => { setAutomationFilter('all'); setInterestFilter('all'); }}
                                            className="w-full text-xs text-stone-500 hover:text-stone-700 py-1 transition-colors"
                                        >
                                            Clear all filters
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => { setShowSortMenu(!showSortMenu); setShowFilterMenu(false); }}
                                className="flex-shrink-0 p-1.5 text-stone-500 rounded-full border border-stone-200 bg-white"
                            >
                                <ArrowUpDown className="w-3.5 h-3.5" />
                            </button>
                            {showSortMenu && (
                                <div className="absolute right-0 mt-1 bg-white border border-notion-border rounded shadow-lg z-50 w-40">
                                    <button
                                        onClick={() => handleSortChange('name')}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-notion-hover transition-colors ${sortBy === 'name' ? 'font-semibold text-stone-900' : 'text-stone-700'
                                            }`}
                                    >
                                        Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </button>
                                    <button
                                        onClick={() => handleSortChange('lastContact')}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-notion-hover transition-colors ${sortBy === 'lastContact' ? 'font-semibold text-stone-900' : 'text-stone-700'
                                            }`}
                                    >
                                        Last Contact {sortBy === 'lastContact' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </button>
                                    <button
                                        onClick={() => handleSortChange('createdAt')}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-notion-hover transition-colors ${sortBy === 'createdAt' ? 'font-semibold text-stone-900' : 'text-stone-700'
                                            }`}
                                    >
                                        Created Date {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-y-auto no-scrollbar bg-stone-50 pb-24 p-4">
                <div className="space-y-3">
                    {filteredLeads.map((lead) => (
                        <MobileLeadCard
                            key={lead.id}
                            lead={lead}
                            executions={executionMap.get(lead.id) || []}
                            onClick={() => openLeadDrawer(lead)}
                            openMenu={openMenu}
                            toggleMenu={toggleMenu}
                            onUpdateStatus={onUpdateStatus}
                            onArchiveLead={onArchiveLead}
                            onUnarchiveLead={onUnarchiveLead}
                            onDeleteLead={onDeleteLead}
                            VALID_STATUSES={VALID_STATUSES}
                        />
                    ))}
                </div>
            </main>

            {showDrawer && (
                <>
                    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 transition-opacity" onClick={closeDrawer} />
                    <MobileLeadDrawer
                        lead={selectedLead}
                        onClose={closeDrawer}
                        onUpdateStatus={onUpdateStatus}
                        onUpdateDetails={onUpdateDetails}
                        onUpdateTags={onUpdateTags}
                        onArchiveLead={onArchiveLead}
                        onUnarchiveLead={onUnarchiveLead}
                        onDeleteLead={onDeleteLead}
                        leads={leads}
                        VALID_STATUSES={VALID_STATUSES}
                    />
                </>
            )}

            {showAddModal && <MobileAddLeadModal onClose={() => setShowAddModal(false)} onAddLead={onAddLead} leads={leads} />}
            {showImportModal && <MobileImportModal onClose={() => setShowImportModal(false)} onImportComplete={() => setShowImportModal(false)} />}
        </>
    );
};

export default MobileLeads;
